import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { ChevronDown, Mail, Loader2, HelpCircle, MessageCircle, ChevronRight } from 'lucide-react';
import { useSchema } from '../hooks/useSchema';
import { useAuth } from '../contexts/AuthContext';
import CoRegulateLogo from '../components/Logo';
import { AdminEditButton } from '../components/admin/AdminEditButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FAQ = () => {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const [pageContent, setPageContent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openSection, setOpenSection] = useState(null);
    
    // Logo links to dashboard if authenticated
    const logoHref = isAuthenticated ? '/home' : '/';

    useEffect(() => {
        window.scrollTo(0, 0);
        const fetchContent = async () => {
            try {
                const response = await fetch(`${API_URL}/api/pages/faq`);
                if (response.ok) {
                    const data = await response.json();
                    setPageContent(data);
                }
            } catch (error) {
                console.error('Error fetching FAQ content:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, []);

    const toggleSection = (sectionId) => {
        setOpenSection(openSection === sectionId ? null : sectionId);
    };

    const visibleSections = pageContent?.sections
        ?.filter(s => s.is_visible !== false)
        .sort((a, b) => a.order - b.order) || [];

    // Generate FAQ schema from visible sections - use useSchema hook
    const faqSchema = visibleSections.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": visibleSections.map(section => ({
            "@type": "Question",
            "name": section.title,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": section.content
            }
        }))
    } : null;

    // Use the custom hook for schema injection (avoids react-helmet bug)
    useSchema(faqSchema);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #FBE0C3 0%, #FEF6EF 100%)' }}>
                <Loader2 className="w-8 h-8 text-[#1D6B6B] animate-spin" />
            </div>
        );
    }

    if (!pageContent) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #FBE0C3 0%, #FEF6EF 100%)' }}>
                <p className="text-[#7D8E95]">Unable to load FAQ content</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen" style={{ background: 'linear-gradient(180deg, #FBE0C3 0%, #FEF6EF 100%)' }}>
            {/* SEO Meta Tags */}
            <Helmet>
                <title>Frequently Asked Questions | CoRegulate ADHD App</title>
                <meta name="description" content="Get answers to common questions about CoRegulate - the ADHD emotional regulation app. Learn about our tools, features, pricing, and how we can help you manage overwhelm." />
                <link rel="canonical" href="https://coregulateadhd.com/faq" />
                <meta property="og:title" content="FAQ | CoRegulate - ADHD Emotional Regulation" />
                <meta property="og:description" content="Common questions about CoRegulate's ADHD tools and features answered." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://coregulateadhd.com/faq" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content="FAQ | CoRegulate" />
                <meta name="twitter:description" content="Common questions about CoRegulate's ADHD tools answered." />
            </Helmet>
            
            {/* Admin Edit Button */}
            <AdminEditButton tab="pages" />
            
            {/* Header - Matching site style */}
            <header className="glass-header sticky top-0 z-50">
                <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
                    <Link to={logoHref} className="flex items-center gap-2 flex-shrink-0" data-testid="header-logo">
                        <CoRegulateLogo size="small" />
                    </Link>
                    {isAuthenticated ? (
                        <Link 
                            to="/home"
                            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white text-xs sm:text-sm font-medium rounded-full hover:shadow-lg transition-all flex-shrink-0 ml-2"
                        >
                            Dashboard
                        </Link>
                    ) : (
                        <Link 
                            to="/auth"
                            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white text-xs sm:text-sm font-medium rounded-full hover:shadow-lg transition-all flex-shrink-0 ml-2"
                        >
                            <span className="hidden sm:inline">Try CoRegulate</span>
                            <span className="sm:hidden">Try Free</span>
                        </Link>
                    )}
                </div>
            </header>

            {/* Hero Section */}
            <section className="py-12 md:py-16 px-4">
                <div className="max-w-3xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 bg-white/60 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                        <HelpCircle className="w-4 h-4 text-[#1D6B6B]" />
                        <span className="text-sm text-[#7D8E95] font-medium">Help Center</span>
                    </div>
                    <h1 
                        className="text-3xl md:text-4xl font-semibold text-[#344648] mb-4"
                        style={{ fontFamily: "'Lora', Georgia, serif" }}
                        data-testid="faq-title"
                    >
                        {pageContent.title || 'Frequently Asked Questions'}
                    </h1>
                    {pageContent.hero_text && (
                        <p className="text-[#7D8E95] text-lg max-w-xl mx-auto">
                            {pageContent.hero_text}
                        </p>
                    )}
                </div>
            </section>

            {/* FAQ Accordion Section */}
            <section className="px-4 pb-16">
                <div className="max-w-3xl mx-auto">
                    {visibleSections.length > 0 ? (
                        <div className="space-y-3">
                            {visibleSections.map((section, index) => (
                                <div 
                                    key={section.section_id}
                                    className="bg-white/60 backdrop-blur-sm rounded-2xl border border-white/50 overflow-hidden transition-all duration-300 hover:border-[#1D6B6B]/20"
                                    data-testid={`faq-item-${section.section_id}`}
                                    style={{ animationDelay: `${index * 50}ms` }}
                                >
                                    <button
                                        onClick={() => toggleSection(section.section_id)}
                                        className="w-full px-6 py-5 flex items-center justify-between text-left group"
                                    >
                                        <h3 className="text-[#344648] font-medium pr-4 group-hover:text-[#1D6B6B] transition-colors">
                                            {section.title}
                                        </h3>
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${openSection === section.section_id ? 'bg-[#1D6B6B] rotate-180' : 'bg-[#FBE0C3]'}`}>
                                            <ChevronDown className={`w-4 h-4 transition-colors ${openSection === section.section_id ? 'text-white' : 'text-[#7D8E95]'}`} />
                                        </div>
                                    </button>
                                    <div className={`overflow-hidden transition-all duration-300 ${openSection === section.section_id ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                        <div className="px-6 pb-6 pt-0">
                                            <div className="h-px bg-gradient-to-r from-[#1D6B6B]/20 via-[#FFBB98]/30 to-transparent mb-4" />
                                            <p className="text-[#7D8E95] leading-relaxed whitespace-pre-line">
                                                {section.content}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <HelpCircle className="w-12 h-12 text-[#7D8E95]/50 mx-auto mb-4" />
                            <p className="text-[#7D8E95]">No FAQs available yet.</p>
                        </div>
                    )}
                </div>
            </section>

            {/* Contact Section */}
            <section className="px-4 pb-16">
                <div className="max-w-3xl mx-auto">
                    <div className="bg-white/40 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/50 text-center">
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#1D6B6B] to-[#145555] flex items-center justify-center mx-auto mb-5">
                            <MessageCircle className="w-6 h-6 text-white" />
                        </div>
                        <h2 className="text-2xl font-semibold text-[#344648] mb-3" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                            Still have questions?
                        </h2>
                        <p className="text-[#7D8E95] mb-6 max-w-md mx-auto">
                            Can't find what you're looking for? We're here to help.
                        </p>
                        {pageContent.contact_email ? (
                            <a 
                                href={`mailto:${pageContent.contact_email}`}
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white px-6 py-3 rounded-full font-medium hover:shadow-lg transition-all"
                                data-testid="faq-contact-email"
                            >
                                <Mail className="w-4 h-4" />
                                Contact Us
                            </a>
                        ) : (
                            <Link 
                                to="/contact"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#1D6B6B] to-[#145555] text-white px-6 py-3 rounded-full font-medium hover:shadow-lg transition-all"
                            >
                                <Mail className="w-4 h-4" />
                                Contact Us
                            </Link>
                        )}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="px-4 pb-16">
                <div className="max-w-3xl mx-auto text-center">
                    <h2 className="text-2xl font-semibold text-[#344648] mb-4" style={{ fontFamily: "'Lora', Georgia, serif" }}>
                        Ready to Start Regulating?
                    </h2>
                    <p className="text-[#7D8E95] mb-6">
                        Join thousands finding calm with CoRegulate.
                    </p>
                    <Link 
                        to="/auth"
                        className="inline-flex items-center gap-2 text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:opacity-90 transition-all"
                        style={{
                            background: 'linear-gradient(135deg, #7D8E95 0%, #2A8585 35%, #1D6B6B 65%, #FFBB98 100%)'
                        }}
                        data-testid="faq-cta-primary"
                    >
                        Get Started Free
                        <ChevronRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            {/* Footer */}
            <footer className="glass-header border-t-0 py-12">
                <div className="max-w-5xl mx-auto px-4 text-center">
                    <CoRegulateLogo size="small" />
                    <p className="text-[#7D8E95] text-sm mt-4 mb-6">
                        Made for ADHD hearts, shaped by ADHD minds.
                    </p>
                    <div className="flex justify-center gap-6 text-sm text-[#7D8E95]">
                        <Link to="/" className="hover:text-[#1D6B6B] transition-colors">Home</Link>
                        <Link to="/about" className="hover:text-[#1D6B6B] transition-colors">About</Link>
                        <Link to="/blog" className="hover:text-[#1D6B6B] transition-colors">Blog</Link>
                        <Link to="/pricing" className="hover:text-[#1D6B6B] transition-colors">Pricing</Link>
                        <Link to="/contact" className="hover:text-[#1D6B6B] transition-colors">Contact</Link>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default FAQ;
